import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';

const FluidBlobVisual = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let w = canvas.width = 400;
    let h = canvas.height = 250;

    let time = 0;
    let frameId;

    const draw = () => {
      time += 0.012;
      ctx.clearRect(0, 0, w, h);

      // Blob 1 (Cyan)
      const x1 = w / 2 + Math.sin(time) * 50;
      const y1 = h / 2 + Math.cos(time * 0.7) * 25;
      const r1 = 80 + Math.sin(time * 0.5) * 12;
      const g1 = ctx.createRadialGradient(x1, y1, 5, x1, y1, r1);
      g1.addColorStop(0, 'rgba(20, 184, 166, 0.18)'); // teal/cyan
      g1.addColorStop(1, 'transparent');
      ctx.fillStyle = g1;
      ctx.beginPath();
      ctx.arc(x1, y1, r1, 0, Math.PI * 2);
      ctx.fill();

      // Blob 2 (Indigo/Purple)
      const x2 = w / 2 + Math.sin(time * 0.8 + 2.0) * 70;
      const y2 = h / 2 + Math.cos(time * 1.1 + 1.0) * 35;
      const r2 = 70 + Math.cos(time * 0.6) * 8;
      const g2 = ctx.createRadialGradient(x2, y2, 5, x2, y2, r2);
      g2.addColorStop(0, 'rgba(99, 102, 241, 0.18)'); // indigo/purple
      g2.addColorStop(1, 'transparent');
      ctx.fillStyle = g2;
      ctx.beginPath();
      ctx.arc(x2, y2, r2, 0, Math.PI * 2);
      ctx.fill();

      // Specular highlight orb
      const x3 = w / 2 + Math.sin(time * 0.5 - 1.0) * 30;
      const y3 = h / 2 + Math.cos(time * 0.8 - 0.5) * 15;
      const r3 = 45;
      const g3 = ctx.createRadialGradient(x3, y3, 0, x3, y3, r3);
      g3.addColorStop(0, 'rgba(255, 255, 255, 0.05)');
      g3.addColorStop(1, 'transparent');
      ctx.fillStyle = g3;
      ctx.beginPath();
      ctx.arc(x3, y3, r3, 0, Math.PI * 2);
      ctx.fill();

      frameId = requestAnimationFrame(draw);
    };

    draw();

    return () => cancelAnimationFrame(frameId);
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 1,
        opacity: 0.9,
      }}
    />
  );
};

const GlassChip = ({ icon, title, value, percent }) => (
  <div style={{
    background: 'rgba(255, 255, 255, 0.015)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    border: '1px solid rgba(255, 255, 255, 0.05)',
    borderRadius: '18px',
    padding: '18px 22px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    textAlign: 'left',
    boxShadow: '0 8px 32px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.05)',
    transformStyle: 'preserve-3d',
    transform: 'translateZ(30px)',
  }}>
    <div style={{
      fontSize: '22px',
      background: 'rgba(255, 255, 255, 0.03)',
      width: '42px',
      height: '42px',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: '1px solid rgba(255, 255, 255, 0.05)',
      flexShrink: 0,
    }}>
      {icon}
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{
        fontSize: '9px',
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: 'var(--ink-3)',
        marginBottom: '3px',
      }}>
        {title}
      </div>
      <div style={{
        fontSize: '14px',
        fontWeight: 800,
        color: 'var(--ink)',
        letterSpacing: '-0.01em',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}>
        {value}
      </div>
      <div style={{
        height: '3px',
        background: 'rgba(255,255,255,0.06)',
        borderRadius: '2px',
        marginTop: '6px',
        overflow: 'hidden',
      }}>
        <div style={{
          height: '100%',
          width: `${percent}%`,
          background: 'linear-gradient(90deg, #14b8a6, #6366f1)',
          borderRadius: '2px',
        }} />
      </div>
    </div>
  </div>
);

/* Animated 3D Liquid Glass Panel — desktop only */
const HeroVisual = () => {
  const containerRef = useRef(null);
  const [rotateX, setRotateX] = useState(0);
  const [rotateY, setRotateY] = useState(0);

  const handleMouseMove = (e) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left - rect.width / 2;
    const y = e.clientY - rect.top - rect.height / 2;
    setRotateX(-y / (rect.height / 2) * 10);
    setRotateY(x / (rect.width / 2) * 10);
  };

  const handleMouseLeave = () => {
    setRotateX(0);
    setRotateY(0);
  };

  return (
    <div style={{
      width: '100%',
      maxWidth: '860px',
      margin: '48px auto 0',
      position: 'relative',
      zIndex: 2,
      perspective: '1000px',
    }}>
      <motion.div
        ref={containerRef}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        animate={{ rotateX, rotateY }}
        transition={{ type: 'spring', stiffness: 200, damping: 20 }}
        style={{
          background: 'rgba(255,255,255,0.015)',
          backdropFilter: 'blur(30px)',
          WebkitBackdropFilter: 'blur(30px)',
          border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: '28px',
          padding: '40px',
          boxShadow: '0 40px 120px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.05)',
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '280px',
          transformStyle: 'preserve-3d',
        }}
      >
        {/* Organic fluid core simulation */}
        <FluidBlobVisual />

        {/* Dynamic floating glass cards inside the capsule */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '20px',
          width: '100%',
          zIndex: 3,
        }}>
          <GlassChip icon="⚡" title="API Response" value="~12ms P99 latency" percent={98} />
          <GlassChip icon="🧠" title="AI Processing" value="98.4% F1 accuracy" percent={95} />
          <GlassChip icon="∞" title="Cloud Infra" value="Auto-scaling cluster" percent={88} />
          <GlassChip icon="🛡️" title="Cyber Security" value="SOC2 aligned system" percent={100} />
        </div>

        {/* Specular glass reflection layer */}
        <div style={{
          position: 'absolute', top: 0, right: 0, left: 0, bottom: 0,
          background: 'linear-gradient(135deg, rgba(255,255,255,0.03) 0%, transparent 60%, rgba(0,0,0,0.15) 100%)',
          pointerEvents: 'none',
          zIndex: 4,
        }} />
      </motion.div>
    </div>
  );
};

const Background3DGraphs = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let w = canvas.width = canvas.offsetWidth;
    let h = canvas.height = canvas.offsetHeight;

    const resize = () => {
      if (canvas) {
        w = canvas.width = canvas.offsetWidth;
        h = canvas.height = canvas.offsetHeight;
      }
    };
    window.addEventListener('resize', resize);

    const line1Points = [];
    const count = 30;
    for (let i = 0; i < count; i++) {
      const pct = i / (count - 1);
      const x = -240 + pct * 480;
      const z = -60 + pct * 120;
      const baseClimb = -60 + Math.pow(pct, 1.8) * 140;
      const noise = Math.sin(i * 1.5) * 8 + Math.cos(i * 2.5) * 5;
      line1Points.push({ x, y: baseClimb + noise, z });
    }

    const line2Points = [];
    for (let i = 0; i < count; i++) {
      const pct = i / (count - 1);
      const x = -220 + pct * 440;
      const z = -40 + pct * 80;
      const baseClimb = -80 + Math.pow(pct, 2.8) * 170;
      const noise = Math.cos(i * 1.8) * 4;
      line2Points.push({ x, y: baseClimb + noise, z });
    }

    const pitch = 56 * Math.PI / 180;
    const yaw = -22 * Math.PI / 180;

    const rotateX = (x, y, z, angle) => {
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      return { x, y: y * cos - z * sin, z: y * sin + z * cos };
    };

    const rotateY = (x, y, z, angle) => {
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      return { x: x * cos + z * sin, y, z: -x * sin + z * cos };
    };

    let animationTime = 0;
    let frameId;

    const draw = () => {
      if (!ctx || !canvas) return;
      ctx.clearRect(0, 0, w, h);

      animationTime += 0.005;

      const centerX = w / 2;
      const centerY = h / 2 + 60;
      const perspective = 350;

      const project = (p) => {
        let r1 = rotateY(p.x, p.y, p.z, yaw);
        let r2 = rotateX(r1.x, r1.y, r1.z, pitch);
        const scale = perspective / (perspective + r2.z);
        return {
          sx: centerX + r2.x * scale,
          sy: centerY - r2.y * scale,
          sz: r2.z
        };
      };

      ctx.lineWidth = 0.8;
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.012)';
      const gridFloorY = -90;
      
      for (let z = -100; z <= 100; z += 25) {
        ctx.beginPath();
        for (let x = -260; x <= 260; x += 10) {
          const pt = project({ x, y: gridFloorY, z });
          if (x === -260) ctx.moveTo(pt.sx, pt.sy);
          else ctx.lineTo(pt.sx, pt.sy);
        }
        ctx.stroke();
      }

      for (let x = -260; x <= 260; x += 40) {
        ctx.beginPath();
        for (let z = -100; z <= 100; z += 10) {
          const pt = project({ x, y: gridFloorY, z });
          if (z === -100) ctx.moveTo(pt.sx, pt.sy);
          else ctx.lineTo(pt.sx, pt.sy);
        }
        ctx.stroke();
      }

      const projLine1 = line1Points.map(p => project(p));
      const projLine2 = line2Points.map(p => project(p));

      ctx.lineWidth = 1;
      ctx.strokeStyle = 'rgba(20, 184, 166, 0.03)';
      for (let i = 0; i < projLine1.length; i += 2) {
        const p1 = line1Points[i];
        const topProj = projLine1[i];
        const botProj = project({ x: p1.x, y: gridFloorY, z: p1.z });
        ctx.beginPath();
        ctx.moveTo(topProj.sx, topProj.sy);
        ctx.lineTo(botProj.sx, botProj.sy);
        ctx.stroke();
      }

      ctx.strokeStyle = 'rgba(99, 102, 241, 0.03)';
      for (let i = 0; i < projLine2.length; i += 3) {
        const p2 = line2Points[i];
        const topProj = projLine2[i];
        const botProj = project({ x: p2.x, y: gridFloorY, z: p2.z });
        ctx.beginPath();
        ctx.moveTo(topProj.sx, topProj.sy);
        ctx.lineTo(botProj.sx, botProj.sy);
        ctx.stroke();
      }

      ctx.lineWidth = 2.5;
      ctx.strokeStyle = 'rgba(20, 184, 166, 0.22)';
      ctx.shadowColor = 'rgba(20, 184, 166, 0.4)';
      ctx.shadowBlur = 8;
      ctx.beginPath();
      projLine1.forEach((p, idx) => {
        if (idx === 0) ctx.moveTo(p.sx, p.sy);
        else ctx.lineTo(p.sx, p.sy);
      });
      ctx.stroke();
      ctx.shadowBlur = 0;

      ctx.lineWidth = 2;
      ctx.strokeStyle = 'rgba(99, 102, 241, 0.18)';
      ctx.shadowColor = 'rgba(99, 102, 241, 0.3)';
      ctx.shadowBlur = 6;
      ctx.beginPath();
      projLine2.forEach((p, idx) => {
        if (idx === 0) ctx.moveTo(p.sx, p.sy);
        else ctx.lineTo(p.sx, p.sy);
      });
      ctx.stroke();
      ctx.shadowBlur = 0;

      const pulseIndex1 = Math.floor((animationTime * 7) % projLine1.length);
      const pulse1 = projLine1[pulseIndex1];
      if (pulse1) {
        ctx.fillStyle = '#14b8a6';
        ctx.shadowColor = '#14b8a6';
        ctx.shadowBlur = 10;
        ctx.beginPath();
        ctx.arc(pulse1.sx, pulse1.sy, 4.5, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;
      }

      const pulseIndex2 = Math.floor((animationTime * 5 + 10) % projLine2.length);
      const pulse2 = projLine2[pulseIndex2];
      if (pulse2) {
        ctx.fillStyle = '#6366f1';
        ctx.shadowColor = '#6366f1';
        ctx.shadowBlur = 8;
        ctx.beginPath();
        ctx.arc(pulse2.sx, pulse2.sy, 4, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;
      }

      frameId = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 1,
        opacity: 0.8,
      }}
    />
  );
};

const Hero = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  return (
    <section className="hero" id="home">
      <div className="hero-bg">
        <Background3DGraphs />
        <div className="hero-orb hero-orb-1" />
        <div className="hero-orb hero-orb-2" />
        <div className="hero-orb hero-orb-3" />
      </div>

      <div className="container">
        <div className="hero-content">
          <motion.div
            className="hero-badge"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <span className="hero-badge-dot" />
            Next-Generation Software Engineering
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
          >
            Engineering the Future<br />
            <span className="hero-text-gradient">of Digital Products</span>
          </motion.h1>

          <motion.p
            className="hero-subtitle"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
          >
            We build scalable SaaS platforms, AI-powered systems, and high-performance APIs.
            Your vision, engineered to perfection.
          </motion.p>

          <motion.div
            className="hero-buttons"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.65, ease: [0.16, 1, 0.3, 1] }}
          >
            <button className="btn-primary" onClick={() => window.location.href = '#cta'}>
              Start Your Project &nbsp;→
            </button>
            <button className="btn-secondary" onClick={() => window.location.href = '#process'}>
              Book a Strategy Call
            </button>
          </motion.div>

          {/* Only render animated SVG on desktop */}
          {!isMobile && <HeroVisual />}
        </div>
      </div>
    </section>
  );
};

export default Hero;
